import { baseApi } from '@services/api/baseApi';
import {
  invalidateTagsOnSuccess,
  invalidateTagsOnSuccessConst,
  providesPaginatedListTags,
  providesTagsOnSuccess,
} from '@services/api/rtkqTagHelpers';
import { type ListQuery, listQueryToParams, type ResponseDataList } from '@shared/api/list';

import type { ManagedProject } from './types';

export type ProjectListQuery = ListQuery & { projectClientId?: string };

export interface CreateProjectRequest {
  projectClientId: string;
  name: string;
  projectCode?: string;
  description?: string;
  status: 'ACTIVE' | 'ON_HOLD' | 'ARCHIVED';
}

export type UpdateProjectRequest = Omit<CreateProjectRequest, 'projectClientId'> & { id: string };

const projectsApiBase = baseApi.enhanceEndpoints({ addTagTypes: ['Project'] });

export const projectsApi = projectsApiBase.injectEndpoints({
  endpoints: (build) => ({
    listProjects: build.query<ResponseDataList<ManagedProject>, ProjectListQuery>({
      query: ({ projectClientId, ...q }) => ({
        url: 'projects',
        params: {
          ...listQueryToParams(q),
          ...(projectClientId ? { project_client_id: projectClientId } : {}),
        },
      }),
      providesTags: providesPaginatedListTags('Project'),
    }),
    getProject: build.query<ManagedProject, string>({
      query: (id) => ({ url: `projects/${id}` }),
      providesTags: providesTagsOnSuccess((_result, id) => [{ type: 'Project' as const, id }]),
    }),
    createProject: build.mutation<ManagedProject, CreateProjectRequest>({
      query: (body) => ({ url: 'projects', method: 'POST', body }),
      invalidatesTags: invalidateTagsOnSuccessConst([{ type: 'Project', id: 'LIST' }]),
    }),
    updateProject: build.mutation<void, UpdateProjectRequest>({
      query: ({ id, ...body }) => ({ url: `projects/${id}`, method: 'PUT', body }),
      invalidatesTags: invalidateTagsOnSuccess((_result, arg) => [
        { type: 'Project', id: arg.id },
        { type: 'Project', id: 'LIST' },
      ]),
    }),
    deleteProject: build.mutation<void, string>({
      query: (id) => ({ url: `projects/${id}`, method: 'DELETE' }),
      invalidatesTags: invalidateTagsOnSuccess((_result, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ]),
    }),
  }),
});

export const {
  useListProjectsQuery,
  useLazyListProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} = projectsApi;
